import type { IAccessPolicyUserList } from 'interfaces/IAccessPolicies';
import type { IApiResponse } from 'interfaces/IApiResponse';
import * as Rx from 'rxjs';
import { map, tap } from 'rxjs/operators';
import type { Group, GroupModule, SaveAccessControl, SaveUser } from 'v2/interfaces/access-control';

import apiService from './api';
import type { ApiService } from './api';

class AccessControlService {
  public groups$ = new Rx.BehaviorSubject<Group[]>(null);
  public isLoadingModules$ = new Rx.BehaviorSubject<boolean>(true);
  public isLoadingGroups$ = new Rx.BehaviorSubject<boolean>(true);

  constructor(private apiService: ApiService) {}

  public getModules(): Rx.Observable<GroupModule[]> {
    this.isLoadingModules$.next(true);
    return this.apiService.get('/access-control/modules').pipe(
      map(response => response.data),
      tap(() => this.isLoadingModules$.next(false))
    );
  }

  public saveGroup(payload: SaveAccessControl) {
    return this.apiService.post('/access-control/groups', payload);
  }

  public updateGroup(payload: Partial<SaveAccessControl>) {
    return this.apiService.post(`/access-control/groups/${payload.id}`, payload);
  }

  public deleteGroup(groupId: number) {
    return this.apiService.delete(`/access-control/groups/${groupId}`);
  }

  public loadGroups(): Rx.Observable<Group[]> {
    this.isLoadingGroups$.next(true);
    return this.apiService.get('/access-control/groups').pipe(
      map(response => response.data.groups),
      tap(groups => {
        this.groups$.next(groups);
        this.isLoadingGroups$.next(false);
      })
    );
  }

  public getGroups(): Rx.Observable<Group[]> {
    return this.groups$;
  }

  public saveUser(payload: SaveUser) {
    return this.apiService.post('/access-control/users', payload);
  }

  public updateUser({ id, ...payload }: Partial<SaveUser>) {
    return this.apiService.post(`/access-control/users/${id}`, payload);
  }

  public deleteUser(id: number) {
    return this.apiService.delete(`/access-control/users/${id}`).toPromise();
  }

  public listUsers() {
    return this.apiService.get<IApiResponse<IAccessPolicyUserList>>('/access-control/users').toPromise();
  }
}

const accessControlService = new AccessControlService(apiService);
export default accessControlService;
